
import React, { useState, useEffect } from 'react';
import { useBooking } from '../../context/BookingContext';
import { Button } from '../ui/button';
import { format } from 'date-fns';
import { TimeSlot, TimeSlotGroup } from '../../types';
import { appointmentService } from '../../services/appointmentService';
import DateSelector from './DateSelector';
import TimeSlotsSection from './TimeSlotsSection';
import { generateTimeSlots } from '../../utils/timeSlotUtils';

const DateTimeSelection: React.FC = () => {
  const { state, selectDate, selectTimeSlot, goToStep } = useBooking();
  const [timeSlotGroups, setTimeSlotGroups] = useState<TimeSlotGroup[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(state.timeSlot);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);

  // Generate time slots when date changes
  useEffect(() => {
    const fetchAvailability = async () => {
      if (state.date && state.doctor) {
        setLoading(true);
        setApiError(false);
        try {
          // Generate slots based on doctor's configuration
          const slots = generateTimeSlots(state.date, state.doctor);
          
          // Format date for API
          const formattedDate = format(state.date, 'yyyy-MM-dd');
          
          // Initialize all slots as available by default
          let updatedGroups = slots.map(group => ({
            ...group,
            slots: group.slots.map(slot => ({
              ...slot,
              available: true
            }))
          }));
          
          // Now check for unavailable slots from API
          try {
            // Fetch existing appointments for this doctor on this date
            const doctorId = state.doctor.id;
            const appointments = await appointmentService.getByDoctor(doctorId);
            
            // Filter appointments for the selected date
            const appointmentsForDate = appointments.filter(
              app => app.date === formattedDate && app.status === 'confirmed'
            );
            
            console.log('Appointments for selected date:', appointmentsForDate);
            
            // Mark booked slots as unavailable
            if (appointmentsForDate.length > 0) {
              updatedGroups = updatedGroups.map(group => ({
                ...group,
                slots: group.slots.map(slot => ({
                  ...slot,
                  available: !appointmentsForDate.some(app => app.timeSlot === slot.time)
                }))
              }));
            }
          } catch (error) {
            console.error('Error fetching availability:', error);
            setApiError(true);
          }
          
          setTimeSlotGroups(updatedGroups);
        } catch (error) {
          console.error('Error generating time slots:', error);
          setApiError(true);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchAvailability();
  }, [state.date, state.doctor]);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      selectDate(date);
      setSelectedTimeSlot(null); // Reset time slot when date changes
    }
  };

  const handleTimeSlotSelect = (timeSlot: TimeSlot) => {
    if (timeSlot.available) {
      setSelectedTimeSlot(timeSlot);
    }
  };

  const handleContinue = () => {
    if (selectedTimeSlot) {
      selectTimeSlot(selectedTimeSlot);
    }
  };

  const handleBack = () => {
    goToStep(1);
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground">Select Appointment Date & Time</h2>
        <p className="text-muted-foreground">
          {state.doctor ? `Scheduling with ${state.doctor.name}` : 'Choose your preferred date and time'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DateSelector 
          selectedDate={state.date} 
          doctor={state.doctor} 
          onDateSelect={handleDateSelect} 
        />

        <TimeSlotsSection
          date={state.date}
          doctor={state.doctor}
          loading={loading}
          apiError={apiError}
          timeSlotGroups={timeSlotGroups}
          selectedTimeSlot={selectedTimeSlot}
          onTimeSlotSelect={handleTimeSlotSelect}
        />
      </div>

      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={handleBack}>
          Back
        </Button>
        <Button 
          onClick={handleContinue} 
          disabled={!selectedTimeSlot || !state.date || loading}
          className="bg-teal-500 hover:bg-teal-600"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default DateTimeSelection;
